/**
 * Rights Layer Strategy Implementation
 *
 * Implements layer-specific logic for Rights (R) layer processing.
 * Rights layer handles document files for legal, licensing, and rights management.
 * This layer requires component selection from other layers.
 */

import {
  BaseLayerStrategy,
  LayerFieldConfig,
  ValidationResult,
} from './LayerStrategy';

export interface RightsLayerMetadata {
  layerType: 'rights';

  // Core rights information
  rightsName?: string;
  rightsType?:
    | 'License'
    | 'Copyright'
    | 'Patent'
    | 'Trademark'
    | 'Usage_Rights'
    | 'Revenue_Share';
  description?: string;

  // Legal framework
  jurisdiction?: string; // legal jurisdiction
  lawFramework?: string; // applicable legal framework
  rightsHolder?: string; // who owns the rights
  grantor?: string; // who is granting the rights
  grantee?: string; // who is receiving the rights

  // Rights scope
  scope?: string[]; // what rights are granted
  restrictions?: string[]; // what restrictions apply
  territory?: string[]; // geographical scope
  duration?: {
    startDate?: Date;
    endDate?: Date;
    renewable?: boolean;
  };

  // Associated assets (NNA addresses from other layers)
  coveredAssets?: {
    songAssets?: string[]; // G layer assets covered by these rights
    starAssets?: string[]; // S layer assets covered by these rights
    lookAssets?: string[]; // L layer assets covered by these rights
    moveAssets?: string[]; // M layer assets covered by these rights
    worldAssets?: string[]; // W layer assets covered by these rights
    compositeAssets?: string[]; // C layer assets covered by these rights
  };

  // Financial terms
  licenseFee?: number;
  royaltyRate?: number; // percentage
  revenueShare?: number; // percentage
  currency?: string;
  paymentTerms?: string;

  // Usage permissions
  commercialUse?: boolean;
  derivativeWorks?: boolean;
  distribution?: boolean;
  publicPerformance?: boolean;
  digitalRights?: boolean;
  printRights?: boolean;

  // Attribution requirements
  attributionRequired?: boolean;
  creditFormat?: string;
  attributionText?: string;

  // Technical metadata
  documentVersion?: string;
  documentHash?: string; // for integrity verification
  digitalSignature?: string;
  witnessSignatures?: string[];

  // Compliance and monitoring
  complianceRequired?: string[]; // compliance requirements
  monitoringRights?: boolean; // right to monitor usage
  auditRights?: boolean; // right to audit usage
  enforcementMechanism?: string[];

  // Legal status
  executionDate?: Date;
  effectiveDate?: Date;
  registrationNumber?: string;
  legalStatus?:
    | 'Draft'
    | 'Pending'
    | 'Active'
    | 'Suspended'
    | 'Expired'
    | 'Terminated';

  // AI analysis
  complexity?: 'Simple' | 'Standard' | 'Complex' | 'Highly_Complex';
  riskLevel?: 'Low' | 'Medium' | 'High' | 'Critical';
  enforceability?: number; // 0-1 how enforceable this agreement is
}

export class RightsLayerStrategy extends BaseLayerStrategy {
  readonly layerCode = 'R';
  readonly layerName = 'Rights';
  readonly layerDescription =
    'Legal rights, licenses, and usage agreements for digital assets';

  getRequiredFields(): string[] {
    return ['rightsName', 'rightsType', 'rightsHolder'];
  }

  getOptionalFields(): string[] {
    return [
      'description',
      'jurisdiction',
      'lawFramework',
      'grantor',
      'grantee',
      'scope',
      'restrictions',
      'territory',
      'duration',
      'coveredAssets',
      'licenseFee',
      'royaltyRate',
      'revenueShare',
      'currency',
      'paymentTerms',
      'commercialUse',
      'derivativeWorks',
      'distribution',
      'publicPerformance',
      'digitalRights',
      'printRights',
      'attributionRequired',
      'creditFormat',
      'attributionText',
      'documentVersion',
      'documentHash',
      'digitalSignature',
      'witnessSignatures',
      'complianceRequired',
      'monitoringRights',
      'auditRights',
      'enforcementMechanism',
      'executionDate',
      'effectiveDate',
      'registrationNumber',
      'legalStatus',
      'complexity',
      'riskLevel',
      'enforceability',
    ];
  }

  getFieldConfig(): LayerFieldConfig {
    return {
      required: this.getRequiredFields(),
      optional: this.getOptionalFields(),
      computed: ['complexity', 'riskLevel', 'enforceability', 'documentHash'],
      inherited: ['jurisdiction', 'lawFramework'],
    };
  }

  extractMetadataFromAI(aiResponse: any): RightsLayerMetadata {
    const layerMetadata =
      aiResponse.layerMetadata || aiResponse.rightsMetadata || {};

    return {
      layerType: 'rights',

      // Core information
      rightsName: layerMetadata.rightsName || aiResponse.rightsName || '',
      rightsType: this.normalizeRightsType(
        layerMetadata.rightsType || aiResponse.rightsType
      ),
      description: layerMetadata.description || aiResponse.description || '',

      // Legal framework
      jurisdiction: layerMetadata.jurisdiction || aiResponse.jurisdiction,
      lawFramework: layerMetadata.lawFramework || aiResponse.lawFramework,
      rightsHolder: layerMetadata.rightsHolder || aiResponse.rightsHolder,
      grantor: layerMetadata.grantor || aiResponse.grantor,
      grantee: layerMetadata.grantee || aiResponse.grantee,

      // Rights scope
      scope: this.normalizeArray(layerMetadata.scope || aiResponse.scope),
      restrictions: this.normalizeArray(
        layerMetadata.restrictions || aiResponse.restrictions
      ),
      territory: this.normalizeArray(
        layerMetadata.territory || aiResponse.territory
      ),
      duration: this.normalizeDuration(
        layerMetadata.duration || aiResponse.duration
      ),

      // Associated assets
      coveredAssets: this.extractCoveredAssets(
        layerMetadata.coveredAssets || aiResponse.coveredAssets
      ),

      // Financial terms
      licenseFee: layerMetadata.licenseFee || aiResponse.licenseFee,
      royaltyRate: layerMetadata.royaltyRate || aiResponse.royaltyRate,
      revenueShare: layerMetadata.revenueShare || aiResponse.revenueShare,
      currency: layerMetadata.currency || aiResponse.currency,
      paymentTerms: layerMetadata.paymentTerms || aiResponse.paymentTerms,

      // Usage permissions
      commercialUse: layerMetadata.commercialUse ?? aiResponse.commercialUse,
      derivativeWorks:
        layerMetadata.derivativeWorks ?? aiResponse.derivativeWorks,
      distribution: layerMetadata.distribution ?? aiResponse.distribution,
      publicPerformance:
        layerMetadata.publicPerformance ?? aiResponse.publicPerformance,
      digitalRights: layerMetadata.digitalRights ?? aiResponse.digitalRights,
      printRights: layerMetadata.printRights ?? aiResponse.printRights,

      // Attribution
      attributionRequired:
        layerMetadata.attributionRequired ?? aiResponse.attributionRequired,
      creditFormat: layerMetadata.creditFormat || aiResponse.creditFormat,
      attributionText:
        layerMetadata.attributionText || aiResponse.attributionText,

      // Technical
      documentVersion:
        layerMetadata.documentVersion || aiResponse.documentVersion,
      documentHash: layerMetadata.documentHash || aiResponse.documentHash,
      digitalSignature:
        layerMetadata.digitalSignature || aiResponse.digitalSignature,
      witnessSignatures: this.normalizeArray(
        layerMetadata.witnessSignatures || aiResponse.witnessSignatures
      ),

      // Compliance
      complianceRequired: this.normalizeArray(
        layerMetadata.complianceRequired || aiResponse.complianceRequired
      ),
      monitoringRights:
        layerMetadata.monitoringRights ?? aiResponse.monitoringRights,
      auditRights: layerMetadata.auditRights ?? aiResponse.auditRights,
      enforcementMechanism: this.normalizeArray(
        layerMetadata.enforcementMechanism || aiResponse.enforcementMechanism
      ),

      // Legal status
      executionDate: this.normalizeDate(
        layerMetadata.executionDate || aiResponse.executionDate
      ),
      effectiveDate: this.normalizeDate(
        layerMetadata.effectiveDate || aiResponse.effectiveDate
      ),
      registrationNumber:
        layerMetadata.registrationNumber || aiResponse.registrationNumber,
      legalStatus: this.normalizeLegalStatus(
        layerMetadata.legalStatus || aiResponse.legalStatus
      ),

      // AI analysis
      complexity: this.normalizeComplexity(
        layerMetadata.complexity || aiResponse.complexity
      ),
      riskLevel: this.normalizeRiskLevel(
        layerMetadata.riskLevel || aiResponse.riskLevel
      ),
      enforceability: layerMetadata.enforceability || aiResponse.enforceability,
    };
  }

  enhanceWithDefaults(metadata: RightsLayerMetadata): RightsLayerMetadata {
    return {
      ...this.getDefaultMetadata(),
      ...metadata,
      layerType: 'rights',

      // Ensure required fields have defaults
      rightsName: metadata.rightsName || 'Untitled Rights Agreement',
      rightsType: metadata.rightsType || 'License',
      rightsHolder: metadata.rightsHolder || 'Unknown',

      // Normalize arrays
      scope: this.normalizeArray(metadata.scope),
      restrictions: this.normalizeArray(metadata.restrictions),
      territory: this.normalizeArray(metadata.territory),
      witnessSignatures: this.normalizeArray(metadata.witnessSignatures),
      complianceRequired: this.normalizeArray(metadata.complianceRequired),
      enforcementMechanism: this.normalizeArray(metadata.enforcementMechanism),

      // Default values
      legalStatus: metadata.legalStatus || 'Draft',
      complexity: metadata.complexity || 'Standard',
      riskLevel: metadata.riskLevel || 'Medium',
      currency: metadata.currency || 'USD',

      // Ensure covered assets object exists
      coveredAssets: metadata.coveredAssets || {},
    };
  }

  generateTags(metadata: RightsLayerMetadata, description?: string): string[] {
    const tags = new Set<string>();

    // Basic layer tags
    tags.add('rights');
    tags.add('legal');
    tags.add('license');

    // Rights type tags
    if (metadata.rightsType) {
      tags.add(metadata.rightsType.toLowerCase().replace(/_/g, '-'));
      switch (metadata.rightsType) {
        case 'License':
          tags.add('licensing');
          tags.add('permission');
          break;
        case 'Copyright':
          tags.add('copyright');
          tags.add('intellectual-property');
          break;
        case 'Patent':
          tags.add('patent');
          tags.add('invention');
          break;
        case 'Trademark':
          tags.add('trademark');
          tags.add('brand');
          break;
        case 'Usage_Rights':
          tags.add('usage');
          tags.add('permission');
          break;
        case 'Revenue_Share':
          tags.add('revenue');
          tags.add('monetization');
          break;
      }
    }

    // Jurisdiction tags
    if (metadata.jurisdiction) {
      tags.add(metadata.jurisdiction.toLowerCase().replace(/\s+/g, '-'));
      tags.add('jurisdiction');
    }

    // Territory tags
    if (metadata.territory && metadata.territory.length > 0) {
      metadata.territory.forEach(territory => {
        tags.add(territory.toLowerCase().replace(/\s+/g, '-'));
      });
      if (
        metadata.territory.some(
          t =>
            t.toLowerCase().includes('global') ||
            t.toLowerCase().includes('worldwide')
        )
      ) {
        tags.add('global');
        tags.add('worldwide');
      }
    }

    // Usage permission tags
    if (metadata.commercialUse) {
      tags.add('commercial-use');
      tags.add('monetizable');
    }

    if (metadata.derivativeWorks) {
      tags.add('derivative-works');
      tags.add('remixable');
    }

    if (metadata.distribution) {
      tags.add('distributable');
      tags.add('shareable');
    }

    if (metadata.publicPerformance) {
      tags.add('public-performance');
      tags.add('performable');
    }

    if (metadata.digitalRights) {
      tags.add('digital-rights');
      tags.add('online');
    }

    // Legal status tags
    if (metadata.legalStatus) {
      tags.add(metadata.legalStatus.toLowerCase());
      switch (metadata.legalStatus) {
        case 'Active':
          tags.add('enforceable');
          tags.add('valid');
          break;
        case 'Expired':
          tags.add('expired');
          tags.add('invalid');
          break;
        case 'Draft':
          tags.add('pending');
          tags.add('preliminary');
          break;
      }
    }

    // Complexity tags
    if (metadata.complexity) {
      tags.add(
        `${metadata.complexity.toLowerCase().replace(/_/g, '-')}-complexity`
      );
    }

    // Risk level tags
    if (metadata.riskLevel) {
      tags.add(`${metadata.riskLevel.toLowerCase()}-risk`);
    }

    // Financial tags
    if (metadata.licenseFee || metadata.royaltyRate || metadata.revenueShare) {
      tags.add('paid');
      tags.add('commercial');
    } else {
      tags.add('free');
    }

    if (metadata.royaltyRate) {
      tags.add('royalty');
      tags.add('percentage-based');
    }

    // Attribution tags
    if (metadata.attributionRequired) {
      tags.add('attribution-required');
      tags.add('credit-required');
    } else {
      tags.add('no-attribution');
    }

    // Compliance tags
    if (metadata.complianceRequired && metadata.complianceRequired.length > 0) {
      tags.add('compliance-required');
      metadata.complianceRequired.forEach(compliance => {
        tags.add(compliance.toLowerCase().replace(/\s+/g, '-'));
      });
    }

    // Monitoring tags
    if (metadata.monitoringRights) {
      tags.add('monitorable');
      tags.add('trackable');
    }

    if (metadata.auditRights) {
      tags.add('auditable');
      tags.add('verifiable');
    }

    // Scope tags
    if (metadata.scope && metadata.scope.length > 0) {
      metadata.scope.forEach(s => {
        tags.add(s.toLowerCase().replace(/\s+/g, '-'));
      });
    }

    // Technical tags
    if (metadata.digitalSignature) {
      tags.add('digitally-signed');
      tags.add('authenticated');
    }

    if (metadata.documentHash) {
      tags.add('hash-verified');
      tags.add('tamper-proof');
    }

    // Asset coverage tags
    if (metadata.coveredAssets) {
      if (metadata.coveredAssets.songAssets?.length) tags.add('covers-audio');
      if (metadata.coveredAssets.starAssets?.length)
        tags.add('covers-characters');
      if (metadata.coveredAssets.lookAssets?.length) tags.add('covers-fashion');
      if (metadata.coveredAssets.moveAssets?.length)
        tags.add('covers-movement');
      if (metadata.coveredAssets.worldAssets?.length)
        tags.add('covers-environments');
      if (metadata.coveredAssets.compositeAssets?.length)
        tags.add('covers-composites');
    }

    return Array.from(tags).slice(0, 30);
  }

  categorizeTags(tags: string[]): {
    primary: string[];
    secondary: string[];
    descriptive: string[];
  } {
    const categories = {
      primary: [] as string[],
      secondary: [] as string[],
      descriptive: [] as string[],
    };

    tags.forEach(tag => {
      const lowerTag = tag.toLowerCase();

      // Primary tags - type and legal framework
      if (
        lowerTag.includes('rights') ||
        lowerTag.includes('license') ||
        lowerTag.includes('copyright') ||
        lowerTag.includes('patent') ||
        lowerTag.includes('trademark')
      ) {
        categories.primary.push(tag);
      }
      // Secondary tags - permissions and status
      else if (
        lowerTag.includes('use') ||
        lowerTag.includes('status') ||
        lowerTag.includes('risk') ||
        lowerTag.includes('complexity')
      ) {
        categories.secondary.push(tag);
      }
      // Descriptive tags - specific details
      else {
        categories.descriptive.push(tag);
      }
    });

    return categories;
  }

  validateBusinessRules(metadata: RightsLayerMetadata): ValidationResult {
    const errors: any[] = [];
    const warnings: any[] = [];

    // Business rule: Rights name should be meaningful
    if (metadata.rightsName && metadata.rightsName.trim().length < 5) {
      warnings.push({
        field: 'rightsName',
        message: 'Rights name should be at least 5 characters long',
        suggestion: 'Provide a more descriptive rights agreement name',
      });
    }

    // Business rule: Should have rights holder
    if (!metadata.rightsHolder || metadata.rightsHolder.trim().length < 2) {
      errors.push({
        field: 'rightsHolder',
        type: 'required',
        message: 'Rights holder must be specified',
        severity: 'error',
      });
    }

    // Business rule: Should have covered assets
    if (metadata.coveredAssets) {
      const hasCoveredAssets =
        (metadata.coveredAssets.songAssets &&
          metadata.coveredAssets.songAssets.length > 0) ||
        (metadata.coveredAssets.starAssets &&
          metadata.coveredAssets.starAssets.length > 0) ||
        (metadata.coveredAssets.lookAssets &&
          metadata.coveredAssets.lookAssets.length > 0) ||
        (metadata.coveredAssets.moveAssets &&
          metadata.coveredAssets.moveAssets.length > 0) ||
        (metadata.coveredAssets.worldAssets &&
          metadata.coveredAssets.worldAssets.length > 0) ||
        (metadata.coveredAssets.compositeAssets &&
          metadata.coveredAssets.compositeAssets.length > 0);

      if (!hasCoveredAssets) {
        warnings.push({
          field: 'coveredAssets',
          message: 'Rights agreement should specify which assets are covered',
          suggestion: 'Select the assets that this rights agreement applies to',
        });
      }
    }

    // Business rule: Duration should be reasonable
    if (
      metadata.duration &&
      metadata.duration.startDate &&
      metadata.duration.endDate
    ) {
      const start = new Date(metadata.duration.startDate);
      const end = new Date(metadata.duration.endDate);

      if (end <= start) {
        errors.push({
          field: 'duration',
          type: 'format',
          message: 'End date must be after start date',
          severity: 'error',
        });
      }

      const durationYears =
        (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 365);
      if (durationYears > 100) {
        warnings.push({
          field: 'duration',
          message: 'Duration exceeds 100 years, which may not be enforceable',
          suggestion: 'Consider shorter duration periods',
        });
      }
    }

    // Business rule: Financial terms should be reasonable
    if (
      metadata.royaltyRate &&
      (metadata.royaltyRate < 0 || metadata.royaltyRate > 100)
    ) {
      warnings.push({
        field: 'royaltyRate',
        message: 'Royalty rate should be between 0% and 100%',
        suggestion: 'Provide a percentage between 0 and 100',
      });
    }

    if (
      metadata.revenueShare &&
      (metadata.revenueShare < 0 || metadata.revenueShare > 100)
    ) {
      warnings.push({
        field: 'revenueShare',
        message: 'Revenue share should be between 0% and 100%',
        suggestion: 'Provide a percentage between 0 and 100',
      });
    }

    // Business rule: Enforceability should be 0-1
    if (
      metadata.enforceability &&
      (metadata.enforceability < 0 || metadata.enforceability > 1)
    ) {
      warnings.push({
        field: 'enforceability',
        message: 'Enforceability should be a value between 0 and 1',
        suggestion: 'Provide a decimal value between 0 and 1',
      });
    }

    // Business rule: High-risk agreements should have compliance requirements
    if (metadata.riskLevel === 'High' || metadata.riskLevel === 'Critical') {
      if (
        !metadata.complianceRequired ||
        metadata.complianceRequired.length === 0
      ) {
        warnings.push({
          field: 'complianceRequired',
          message:
            'High-risk agreements should specify compliance requirements',
          suggestion: 'Add compliance requirements for risk management',
        });
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  getDefaultMetadata(): RightsLayerMetadata {
    return {
      layerType: 'rights',
      rightsName: '',
      rightsType: 'License',
      rightsHolder: '',
      description: '',
      scope: [],
      restrictions: [],
      territory: [],
      witnessSignatures: [],
      complianceRequired: [],
      enforcementMechanism: [],
      legalStatus: 'Draft',
      complexity: 'Standard',
      riskLevel: 'Medium',
      currency: 'USD',
      coveredAssets: {},
    };
  }

  getInheritanceRules(): {
    [field: string]: 'always' | 'if_empty' | 'never' | 'merge';
  } {
    return {
      // Rights are unique legal documents
      rightsName: 'never',
      rightsHolder: 'never',
      coveredAssets: 'never',

      // Could inherit legal framework from parent agreements
      jurisdiction: 'if_empty',
      lawFramework: 'if_empty',
      complianceRequired: 'merge',
    };
  }

  private normalizeArray(value: any): string[] {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      return value
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0);
    }
    return [];
  }

  private normalizeDate(value: any): Date | undefined {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    if (typeof value === 'string') {
      const date = new Date(value);
      return isNaN(date.getTime()) ? undefined : date;
    }
    return undefined;
  }

  private normalizeRightsType(value: any): RightsLayerMetadata['rightsType'] {
    if (!value) return 'License';

    const normalized = String(value).toLowerCase().replace(/[\s-]/g, '_');

    if (normalized.includes('copyright')) return 'Copyright';
    if (normalized.includes('patent')) return 'Patent';
    if (normalized.includes('trademark')) return 'Trademark';
    if (normalized.includes('usage') || normalized.includes('use'))
      return 'Usage_Rights';
    if (normalized.includes('revenue') || normalized.includes('share'))
      return 'Revenue_Share';

    return 'License';
  }

  private normalizeLegalStatus(value: any): RightsLayerMetadata['legalStatus'] {
    if (!value) return 'Draft';

    const normalized = String(value).toLowerCase();

    if (normalized.includes('pending')) return 'Pending';
    if (normalized.includes('active') || normalized.includes('valid'))
      return 'Active';
    if (normalized.includes('suspended')) return 'Suspended';
    if (normalized.includes('expired')) return 'Expired';
    if (normalized.includes('terminated')) return 'Terminated';

    return 'Draft';
  }

  private normalizeComplexity(value: any): RightsLayerMetadata['complexity'] {
    if (!value) return 'Standard';

    const normalized = String(value).toLowerCase().replace(/_/g, ' ');

    if (normalized.includes('simple') || normalized.includes('basic'))
      return 'Simple';
    if (normalized.includes('complex') || normalized.includes('advanced'))
      return 'Complex';
    if (normalized.includes('highly') || normalized.includes('very'))
      return 'Highly_Complex';

    return 'Standard';
  }

  private normalizeRiskLevel(value: any): RightsLayerMetadata['riskLevel'] {
    if (!value) return 'Medium';

    const normalized = String(value).toLowerCase();

    if (normalized.includes('low') || normalized.includes('minimal'))
      return 'Low';
    if (normalized.includes('high') || normalized.includes('significant'))
      return 'High';
    if (normalized.includes('critical') || normalized.includes('severe'))
      return 'Critical';

    return 'Medium';
  }

  private normalizeDuration(value: any): RightsLayerMetadata['duration'] {
    if (!value) return undefined;

    if (typeof value === 'object') {
      return {
        startDate: this.normalizeDate(value.startDate || value.start),
        endDate: this.normalizeDate(value.endDate || value.end),
        renewable: value.renewable ?? false,
      };
    }

    return undefined;
  }

  private extractCoveredAssets(
    assets: any
  ): RightsLayerMetadata['coveredAssets'] {
    if (!assets) return {};

    return {
      songAssets: this.normalizeArray(
        assets.songAssets || assets.songs || assets.G
      ),
      starAssets: this.normalizeArray(
        assets.starAssets || assets.stars || assets.S
      ),
      lookAssets: this.normalizeArray(
        assets.lookAssets || assets.looks || assets.L
      ),
      moveAssets: this.normalizeArray(
        assets.moveAssets || assets.moves || assets.M
      ),
      worldAssets: this.normalizeArray(
        assets.worldAssets || assets.worlds || assets.W
      ),
      compositeAssets: this.normalizeArray(
        assets.compositeAssets || assets.composites || assets.C
      ),
    };
  }
}

// Export singleton instance
export const rightsLayerStrategy = new RightsLayerStrategy();
